/**
 * Utility for confirming Solana transactions on-chain.
 *
 * Primary: WebSocket signatureSubscribe (near-zero RPC calls).
 * Fallback: HTTP polling via getBlockHeight + getTransaction.
 */

import { CacheDuration } from "@solomon-labs/caching";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaTransactionSignature } from "@solomon-labs/types";

import { WebSocketManager } from "../services/websocket";
import type { SignatureNotification } from "../services/websocket";

export interface ConfirmTransactionOptions {
    /** Block height after which the transaction is considered expired */
    lastValidBlockHeight: number;
    /** Polling interval in milliseconds (default: 1500) */
    pollingIntervalMs?: number;
    /** Commitment level for confirmation (default: "confirmed") */
    commitment?: "confirmed" | "finalized";
}

export interface ConfirmTransactionResult {
    /** Whether the transaction was confirmed successfully */
    confirmed: boolean;
    /** Error message if confirmation failed */
    error?: string;
}

const DEFAULT_POLLING_INTERVAL_MS = 1500;
const MAX_TIMEOUT_MS = 90_000;
const BLOCK_HEIGHT_CHECK_INTERVAL_MS = 5_000;
const UNSUPPORTED_VERSION_REGEX = /unsupported.*version/i;

/**
 * Confirm a transaction, preferring WebSocket subscription with HTTP polling fallback.
 */
export async function confirmTransaction(
    signature: SolanaTransactionSignature,
    options: ConfirmTransactionOptions
): Promise<ConfirmTransactionResult> {
    // Try WebSocket first
    try {
        const wsResult = await confirmViaWebSocket(signature, options);
        if (wsResult !== null) {
            return wsResult;
        }
    } catch {
        // WebSocket path failed — fall through to polling
    }

    return confirmViaPolling(signature, options);
}

// ---------------------------------------------------------------------------
// WebSocket confirmation
// ---------------------------------------------------------------------------

async function confirmViaWebSocket(
    signature: SolanaTransactionSignature,
    options: ConfirmTransactionOptions
): Promise<ConfirmTransactionResult | null> {
    const { lastValidBlockHeight, commitment = "confirmed" } = options;

    const client = await WebSocketManager.ensureConnected();
    if (!client) {
        return null; // WS unavailable — caller will fall back to polling
    }

    return new Promise<ConfirmTransactionResult | null>((resolve) => {
        let resolved = false;
        let subscriptionId: number | null = null;
        let absoluteTimer: ReturnType<typeof setTimeout> | null = null;
        let blockHeightTimer: ReturnType<typeof setInterval> | null = null;

        // If the socket drops, settle with null so the caller falls through to polling
        const removeDisconnectListener = client.onDisconnect(() => settle(null));

        function cleanup(): void {
            removeDisconnectListener();
            if (absoluteTimer) {
                clearTimeout(absoluteTimer);
                absoluteTimer = null;
            }
            if (blockHeightTimer) {
                clearInterval(blockHeightTimer);
                blockHeightTimer = null;
            }
            // Defensively unsubscribe (Solana auto-removes on confirm, but be safe)
            if (subscriptionId !== null && client) {
                client.signatureUnsubscribe(subscriptionId).catch(() => {});
                subscriptionId = null;
            }
        }

        function settle(result: ConfirmTransactionResult | null): void {
            if (resolved) return;
            resolved = true;
            cleanup();
            resolve(result);
        }

        // 90s absolute timeout
        absoluteTimer = setTimeout(() => {
            settle({
                confirmed: false,
                error: "Could not confirm transaction. Check explorer."
            });
        }, MAX_TIMEOUT_MS);

        // Periodic block height safety check
        blockHeightTimer = setInterval(() => {
            ConnectionManager.getRpcClient()
                .getBlockHeight("confirmed")
                .then((height) => {
                    if (height !== null && height > lastValidBlockHeight) {
                        settle({
                            confirmed: false,
                            error: "Transaction expired. Please try again."
                        });
                    }
                })
                .catch(() => {
                    // Ignore — the absolute timeout is the safety net
                });
        }, BLOCK_HEIGHT_CHECK_INTERVAL_MS);

        // Subscribe to signature notifications
        client
            .signatureSubscribe(
                signature,
                (notification: SignatureNotification) => {
                    // "receivedSignature" means the node saw the sig but hasn't confirmed yet
                    if (notification.value === "receivedSignature") {
                        return;
                    }

                    if (notification.value.err === null) {
                        settle({ confirmed: true });
                    } else {
                        settle({
                            confirmed: false,
                            error: "Transaction failed. Please try again."
                        });
                    }
                },
                { commitment, enableReceivedNotification: true }
            )
            .then((id) => {
                subscriptionId = id;
                // If already resolved before subscribe returned, clean up
                if (resolved) {
                    client.signatureUnsubscribe(id).catch(() => {});
                }
            })
            .catch(() => {
                // Subscribe failed — fall back to polling
                settle(null);
            });
    });
}

// ---------------------------------------------------------------------------
// HTTP polling confirmation (original logic, unchanged)
// ---------------------------------------------------------------------------

async function confirmViaPolling(
    signature: SolanaTransactionSignature,
    options: ConfirmTransactionOptions
): Promise<ConfirmTransactionResult> {
    const { lastValidBlockHeight, pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS, commitment = "confirmed" } = options;

    const startTime = Date.now();

    while (true) {
        // Check absolute timeout
        if (Date.now() - startTime > MAX_TIMEOUT_MS) {
            return {
                confirmed: false,
                error: "Could not confirm transaction. Check explorer."
            };
        }

        try {
            // Check if blockhash has expired - always use "confirmed" since finalized lags behind
            const currentBlockHeight = await ConnectionManager.getRpcClient().getBlockHeight("confirmed");
            if (currentBlockHeight !== null && currentBlockHeight > lastValidBlockHeight) {
                // If finalized commitment is lagging, check confirmed before failing
                if (commitment === "finalized") {
                    const confirmedTx = await ConnectionManager.getTransaction(
                        signature,
                        {
                            commitment: "confirmed",
                            maxSupportedTransactionVersion: 0
                        },
                        {
                            cacheDuration: CacheDuration.Bust
                        }
                    );
                    if (confirmedTx !== null && confirmedTx.meta?.err === null) {
                        return { confirmed: true };
                    }
                }
                return {
                    confirmed: false,
                    error: "Transaction expired. Please try again."
                };
            }

            // Check transaction status
            const txResponse = await ConnectionManager.getTransaction(
                signature,
                {
                    commitment,
                    maxSupportedTransactionVersion: 0
                },
                {
                    cacheDuration: CacheDuration.Bust
                }
            );

            if (txResponse !== null) {
                // Transaction found
                if (txResponse.meta?.err === null) {
                    // Success - transaction confirmed without errors
                    return { confirmed: true };
                } else {
                    // Transaction failed on-chain
                    return {
                        confirmed: false,
                        error: "Transaction failed. Please try again."
                    };
                }
            }

            // Transaction not found yet, wait and retry
            await sleep(pollingIntervalMs);
        } catch (err) {
            // Check for unsupported transaction version error - fail immediately
            if (err instanceof Error && UNSUPPORTED_VERSION_REGEX.test(err.message)) {
                return {
                    confirmed: false,
                    error: "Unsupported transaction version. Please update the app."
                };
            }
            // Other RPC errors - wait and retry
            await sleep(pollingIntervalMs);
        }
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
